import { useCallback, useEffect, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { Loader2, Pause, Play, X } from "lucide-react";
import { NoArtwork } from "@/components/shared/NoArtwork";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { formatTime, usePlayerStore } from "@/lib/player";
import {
  PREVIEW_UNAVAILABLE_LABEL,
  SIGNED_OUT_PREVIEW_CAPTION,
  forgetPublicPreview,
  loadPublicPreview,
  peekPublicPreview,
  type PublicPreview,
} from "@/lib/public-preview";
import { setPublicPreviewElement } from "@/lib/public-preview-element";
import { useAuth } from "@/providers/AuthProvider";

/** The preview for the player's current track. */
type Cue =
  | { trackId: string; status: "loading" | "unavailable" }
  | { trackId: string; status: "ready"; preview: PublicPreview };

/** `MediaError.MEDIA_ERR_DECODE`: the file itself is broken, so a fresh URL can't help. */
const MEDIA_ERR_DECODE = 3;

const PREVIEW_NEEDS_TAP = "Press play to listen";

function isAutoplayBlocked(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === "NotAllowedError";
}

/**
 * The player for every listener who isn't a fully set-up account (signed-out visitors, unfinished
 * sign-ups, blocked accounts): what a row's Play starts on a share page, a storefront, the shared
 * track page's Versions tab or a public playlist. `PlayerProvider` mounts it in place of the app's
 * player for them (`useListeningPath`); a set-up account never sees it and keeps the clean,
 * signed-in playback.
 *
 * It plays the app player's current track (the same store, so every row's play/pause icon and the
 * share page's listening analytics follow it) from the PUBLIC preview only
 * (`src/lib/public-preview.ts`): the whole track, to the end, with no cap (owner decision,
 * 2026-10-13); the next track in the queue follows on. A signed-out listener reads
 * SIGNED_OUT_PREVIEW_CAPTION and gets a Sign in button. A preview that can't be had says "Preview
 * unavailable", and Play asks again. It never plays another source.
 *
 * One `<audio>` element for every track, never remounted: once a tap has started it, iOS lets it
 * play the next track without one. A row's tap can start a fetched preview on it directly
 * (`startPublicPreviewInTap`). A URL that stops working (signed URLs expire) is fetched again once
 * and resumes where it was. A disco import's reference tracks have no public preview, so they
 * aren't played here.
 */
export function PublicPreviewPlayer() {
  const { user } = useAuth();
  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const trackId =
    currentTrack && currentTrack.kind !== "reference" ? String(currentTrack.id) : null;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [cueState, setCueState] = useState<Cue | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);
  const [needsTap, setNeedsTap] = useState(false);
  /** The one fresh URL after a media error, per URL that loaded. */
  const refreshed = useRef(false);
  /** Where a refreshed URL picks up. */
  const resumeAt = useRef<number | null>(null);

  const cue = cueState !== null && cueState.trackId === trackId ? cueState : null;
  const preview = cue?.status === "ready" ? cue.preview : null;
  const url = preview?.url ?? null;

  const attachAudio = useCallback((element: HTMLAudioElement | null) => {
    // Unmounted (the listener became a set-up account): the preview stops before the app's
    // player takes over.
    if (!element) audioRef.current?.pause();
    audioRef.current = element;
    setPublicPreviewElement(element);
  }, []);

  // A new track starts clean.
  useEffect(() => {
    setTime(0);
    setLength(0);
    setNeedsTap(false);
    refreshed.current = false;
    resumeAt.current = null;
  }, [trackId]);

  // Its preview: at once when it was fetched ahead, otherwise asked for. Asked again on Play after
  // it was unavailable, and once after a URL failed.
  useEffect(() => {
    if (!trackId) {
      setCueState(null);
      return;
    }
    const remembered = peekPublicPreview(trackId);
    if (remembered) {
      setCueState({ trackId, status: "ready", preview: remembered });
      return;
    }
    setCueState({ trackId, status: "loading" });
    const controller = new AbortController();
    void loadPublicPreview(trackId, { signal: controller.signal }).then((loaded) => {
      if (controller.signal.aborted) return;
      setCueState(
        loaded ? { trackId, status: "ready", preview: loaded } : { trackId, status: "unavailable" }
      );
      // Nothing is playing: the rows' icons say so.
      if (!loaded) usePlayerStore.getState().pauseTrack();
    });
    return () => controller.abort();
  }, [trackId, attempt]);

  // The element follows the cue and the player: its source, and playing or not.
  const cueKnown = cue !== null;
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!url) {
      // The render right after a track was chosen doesn't know its cue yet: leave the element
      // alone, as the tap may just have started that very track on it (startPublicPreviewInTap).
      if (trackId !== null && !cueKnown) return;
      // No preview for this track (yet): silence the last one. Removing `src` doesn't stop a
      // media element; load() lets go of the file.
      if (audio.getAttribute("src") !== null) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      return;
    }
    if (audio.getAttribute("src") !== url) audio.src = url;
    if (!isPlaying) {
      audio.pause();
      return;
    }
    void audio.play().then(
      () => setNeedsTap(false),
      (error: unknown) => {
        // iOS refuses a first play() that isn't inside a tap: the next press of Play is one.
        if (!isAutoplayBlocked(error)) return;
        setNeedsTap(true);
        usePlayerStore.getState().pauseTrack();
      }
    );
  }, [url, isPlaying, trackId, cueKnown]);

  // A seek asked for outside the player (`requestSeek`, 0-100), as the app's player applies one to
  // its own element in PlayerProvider: whichever player is playing takes it.
  const seekRequest = usePlayerStore((state) => state.seekRequest);
  useEffect(() => {
    const audio = audioRef.current;
    if (!seekRequest || !audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    audio.currentTime = (seekRequest.position / 100) * audio.duration;
    setTime(audio.currentTime);
  }, [seekRequest]);

  const handleTimeUpdate = (event: SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    setTime(audio.currentTime);
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      usePlayerStore.getState().setPosition((audio.currentTime / audio.duration) * 100);
    }
  };

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setLength(audio.duration);
      usePlayerStore.getState().setDuration(audio.duration);
    }
    if (resumeAt.current !== null) {
      audio.currentTime = resumeAt.current;
      resumeAt.current = null;
    }
  };

  const handleEnded = () => {
    const player = usePlayerStore.getState();
    if (player.queue.length > 0) {
      player.nextTrack();
      return;
    }
    player.pauseTrack();
    player.setPosition(0);
  };

  const handleError = (event: SyntheticEvent<HTMLAudioElement>) => {
    if (!trackId || !url) return;
    const broken = event.currentTarget.error?.code === MEDIA_ERR_DECODE;
    if (refreshed.current || broken) {
      setCueState({ trackId, status: "unavailable" });
      usePlayerStore.getState().pauseTrack();
      return;
    }
    // Signed URLs expire: ask once more, past what was remembered, and carry on from here.
    refreshed.current = true;
    resumeAt.current = event.currentTarget.currentTime || null;
    forgetPublicPreview(trackId);
    setAttempt((count) => count + 1);
  };

  const togglePlay = () => {
    const player = usePlayerStore.getState();
    if (cue?.status === "unavailable") {
      setAttempt((count) => count + 1);
      player.resumeTrack();
      return;
    }
    if (player.isPlaying) {
      player.pauseTrack();
      return;
    }
    // Inside the click, before anything else: a browser that wants a gesture gets one.
    const audio = audioRef.current;
    if (audio && url) void audio.play().catch(() => undefined);
    player.resumeTrack();
  };

  const seek = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !url) return;
    audio.currentTime = seconds;
    setTime(seconds);
  };

  const element = (
    <audio
      ref={attachAudio}
      preload="auto"
      hidden
      onTimeUpdate={handleTimeUpdate}
      onLoadedMetadata={handleLoadedMetadata}
      onLoadedData={() => {
        refreshed.current = false;
      }}
      onEnded={handleEnded}
      onError={handleError}
    />
  );

  if (!currentTrack || !trackId) return element;

  const artwork = currentTrack.cover_art_url || currentTrack.artwork_url || null;
  const busy = !cue || cue.status === "loading";
  const playing = isPlaying && Boolean(url) && !needsTap;
  const signedOut = !user;

  let status: string;
  if (!preview) {
    status = cue?.status === "unavailable" ? PREVIEW_UNAVAILABLE_LABEL : "Loading preview…";
  } else if (needsTap) status = PREVIEW_NEEDS_TAP;
  else status = signedOut ? SIGNED_OUT_PREVIEW_CAPTION : "Preview";

  return (
    <>
      {element}
      <section
        aria-label="Preview player"
        className="fixed bottom-6 left-1/2 z-50 w-[min(640px,calc(100vw-2rem))] -translate-x-1/2"
      >
        <div className="flex items-center gap-4 rounded-3xl border border-border bg-background/95 px-5 py-3 text-foreground shadow-2xl backdrop-blur-md">
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full">
            {artwork ? (
              <img src={artwork} alt="" className="h-full w-full object-cover" />
            ) : (
              <NoArtwork className="h-full w-full" iconSize="w-5 h-5" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <p className="truncate text-sm font-medium">{currentTrack.title}</p>
              {currentTrack.artist ? (
                <p className="truncate text-[13px] text-muted-foreground">{currentTrack.artist}</p>
              ) : null}
            </div>
            <p
              role="status"
              className="mt-0.5 flex items-center gap-1.5 truncate text-[13px] text-muted-foreground"
            >
              {busy ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden /> : null}
              <span className="truncate">{status}</span>
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
                {formatTime(time)}
              </span>
              <Slider
                variant="theme-aware"
                aria-label="Seek"
                value={[time]}
                min={0}
                max={length > 0 ? length : 1}
                step={0.1}
                disabled={!url || length <= 0}
                onValueChange={(values) => setTime(values[0])}
                onValueCommit={(values) => seek(values[0])}
                className="flex-1"
              />
              <span className="w-9 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {formatTime(length)}
              </span>
            </div>
          </div>

          <Button
            variant="secondary"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
            onClick={togglePlay}
            disabled={busy}
            aria-label={playing ? "Pause preview" : "Play preview"}
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
          </Button>

          {signedOut ? (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 rounded-full"
              onClick={() => usePlayerStore.getState().setShowAuthModal(true)}
            >
              Sign in
            </Button>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => usePlayerStore.getState().clearTrack()}
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </>
  );
}
